import { useEffect, useRef } from "react"
import useLogout, { clearUserLocalStorage } from "aldoo-ra/PlatformAuth/actions/logout"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import Value from "aldoo-ra/Value"
import PubSub from "pubsub-js"

// Global state to prevent multiple SessionMonitor instances
let globalDialogShown = false
let globalIsProcessing = false
let globalEventListenersAttached = false
let globalEventHandlers = null
let activeInstances = 0

/**
 * SessionMonitor - Monitors API responses for session invalidation
 *
 * This component listens for API response events and detects when a user's
 * session has been invalidated (e.g., account deleted from mobile app).
 *
 * Key features:
 * - Singleton pattern to prevent multiple instances
 * - Listens for API_RESPONSE_ERROR and API_RESPONSE_DATA events
 * - Detects "invalid_user" errors specifically
 * - Shows dialog only once to prevent spam
 * - Triggers logout to clear local storage and reset systems
 */
const SessionMonitor = () => {
  const logout = useLogout()
  const [profile, setProfile] = Value("user-profile")
  const instanceId = useRef(Math.random().toString(36).substring(7))

  useEffect(() => {
    activeInstances++
    const currentInstanceId = instanceId.current

    // Only the first instance should set up event handlers
    if (!globalEventListenersAttached) {
      const handleAPIResponseError = (event) => {
        const { responseData, url, originalPayload, currentPageUrl } = event.detail || {}

        // Check specifically for invalid_user error
        if (responseData?.error === "invalid_user") {
          // Skip processing if this is a verify or login command - these are expected to return invalid_user
          // during the OAuth signup flow (user doesn't exist yet, will be created)
          if (originalPayload && (originalPayload.verify || originalPayload.login)) {
            return
          }

          handleInvalidUserSession(responseData, url, currentPageUrl)
        }
      }

      const handleAPIResponseData = (event) => {
        const { responseData, url, originalPayload, currentPageUrl } = event.detail || {}

        // Check if response data contains error field with invalid_user
        if (responseData?.error === "invalid_user") {
          // Skip processing if this is a verify command - these are expected to return invalid_user
          if (originalPayload && originalPayload.verify) {
            return
          }

          handleInvalidUserSession(responseData, url, currentPageUrl)
        }
      }

      const handleInvalidUserSession = async (responseData, url, pageUrlAtTimeOfCall) => {
        // Prevent multiple simultaneous processing (global state)
        if (globalIsProcessing) {
          return
        }

        // Prevent showing dialog multiple times (global state)
        if (globalDialogShown) {
          return
        }

        // Use the page URL at the time of API call for redirect after successful login
        const currentUrl = pageUrlAtTimeOfCall || window.location.href

        console.log("🚨 SessionMonitor: INVALID USER SESSION DETECTED!", {
          url,
          error: responseData.error,
          message: responseData.message || "User session has expired",
          currentUrl
        })

        globalIsProcessing = true
        globalDialogShown = true

        try {
          // Clear both in-memory and localStorage
          setProfile(null)
          clearUserLocalStorage()

          const dialogResult = await showPromptDialogAsync({
            title: "Session Expired",
            message: "Your session has expired. Please login again or return to home page.",
            confirmText: "Login", // Yes button becomes "Login"
            cancelText: "Return to Home" // No button becomes "Return to Home"
          })

          // Perform logout to clear local storage and reset systems
          await logout()

          // Handle user's choice after logout
          // dialogResult: true = Login, false = Return to Home
          if (dialogResult === true) {
            // Trigger signin dialog using PubSub with the captured URL for redirect after login
            PubSub.publish("signin", { redirect: currentUrl, forceDialog: true })
          } else if (dialogResult === false) {
            window.location.href = "/"
          }
          // If dialog is closed (dialogResult === null), stay on current page

        } catch (error) {
          console.error("❌ SessionMonitor: Error during logout process:", error)
        } finally {
          globalIsProcessing = false
          // Reset dialog flag after some time in case user navigates back
          setTimeout(() => {
            globalDialogShown = false
          }, 30000) // Reset after 30 seconds
        }
      }

      // Store handlers globally so they persist across remounts
      globalEventHandlers = {
        handleAPIResponseError,
        handleAPIResponseData
      }

      // Add event listeners
      window.addEventListener("API_RESPONSE_ERROR", handleAPIResponseError)
      window.addEventListener("API_RESPONSE_DATA", handleAPIResponseData)

      globalEventListenersAttached = true
    }

    // Cleanup function
    return () => {
      activeInstances--

      // Only remove event listeners when NO instances remain
      if (activeInstances === 0 && globalEventListenersAttached && globalEventHandlers) {
        window.removeEventListener("API_RESPONSE_ERROR", globalEventHandlers.handleAPIResponseError)
        window.removeEventListener("API_RESPONSE_DATA", globalEventHandlers.handleAPIResponseData)
        globalEventListenersAttached = false
        globalEventHandlers = null
      }
    }
  }, [logout, instanceId])

  // SessionMonitor doesn't render any UI
  return null
}

export default SessionMonitor